import React from 'react';
import styled, { css } from 'styled-components';
import { Button as _Button, TextButton } from 'components/core';
import { space, SpaceProps } from 'styled-system';
import {
  FormSubmitButton as FormSubmitButtonOrg,
  FormSubmitButtonProps,
} from 'components/form-inputs/form-submit-button/form-submit-button';
import useTranslation from 'hooks/use-translation';
import { ArrowIcon } from 'dutchie-pay/components/index';
import { Clickable } from 'shared/components';

export const DutchiePayTextButton = styled(TextButton)`
  font-size: 14px;
  padding: 0;
  ${space}
`;

export type DutchiePayButtonContainerProps = FormSubmitButtonProps &
  SpaceProps & {
    showBackLink?: boolean;
    onBackLinkClick?: () => void;
    onClick?: (mxAcctGuid: string) => void;
    buttonText?: string;
    formFields?: boolean;
    disabled?: boolean;
  };

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  button {
    width: ${(props) => (props.showBackLink ? '70%' : '100%')};
  }

  ${space}
`;

export const DutchiePayButtonContainer = ({
  showBackLink,
  onBackLinkClick,
  requiredFieldKeys,
  formFields = true,
  onClick,
  buttonText = `continue`,
  disabled,
  ...props
}: DutchiePayButtonContainerProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <ButtonContainer {...props} showBackLink={showBackLink}>
      {showBackLink && (
        <BackLink aria-label='back' type='button' onClick={onBackLinkClick}>
          <ArrowIcon />
          {t('common.back', 'Back')}
        </BackLink>
      )}
      {formFields ? (
        <FormSubmitButton aria-label='continue' requiredFieldKeys={requiredFieldKeys}>
          {t('common.continue', '{{buttonText}}', { buttonText })}
        </FormSubmitButton>
      ) : (
        <Button type='button' size='large' variant='dutchiePay' onClick={onClick} disabled={disabled}>
          {t('common.continue', '{{buttonText}}', { buttonText })}
        </Button>
      )}
    </ButtonContainer>
  );
};

export const DutchiePayConfirmationButton = styled(_Button).attrs(() => ({
  'aria-label': `confirm`,
  type: `button`,
}))`
  width: 100%;
  background: ${({ theme }) => theme.colors.dutchiePayGreen};
  padding: 16px 24px;
  text-transform: uppercase;
`;

export const FormSubmitButton = (props): JSX.Element => <FormSubmitButtonOrg variant='dutchiePay' {...props} />;

const BackLink = styled(Clickable)`
  ${({ theme: { colors } }) => css`
    color: ${colors.grey[45]};
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    font-weight: 700;
    font-size: 13px;
    width: unset;

    &:hover {
      color: ${colors.grey[30]};
      cursor: pointer;
    }
  `}
`;

const Button = styled(_Button)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.dutchiePayGreen};
  :disabled {
    background: ${({ theme }) => theme.colors.blueGrey[80]};
  }
`;
